import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import axiosInstance from "api/axiosInstance";
import { API_ENDPOINTS } from "api/endpoints";
import {
  ApiListResponse,
  Market,
  MarketPayload,
  MarketsState,
} from "types/market";

const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ message?: string }>;

    if (!axiosError.response) {
      return "Network error. Please try again.";
    }

    return (
      axiosError.response.data?.message ||
      "Something went wrong. Please try again."
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
};

export const extractUploadedImageUrl = (responseData: unknown): string | null => {
  if (typeof responseData === "string" && responseData.trim()) {
    return responseData.trim();
  }

  if (!responseData || typeof responseData !== "object") {
    return null;
  }

  const data = responseData as {
    url?: unknown;
    path?: unknown;
    data?: {
      url?: unknown;
      fileUrl?: unknown;
      path?: unknown;
    };
  };

  const candidates = [
    data.url,
    data.data?.url,
    data.data?.fileUrl,
    data.data?.path,
    data.path,
  ];

  const url = candidates.find(
    (candidate): candidate is string =>
      typeof candidate === "string" && candidate.trim().length > 0
  );

  return url?.trim() || null;
};

export const fetchMarkets = createAsyncThunk<
  Market[],
  void,
  { rejectValue: string }
>("markets/fetchMarkets", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<ApiListResponse<Market[]>>(
      API_ENDPOINTS.markets.list
    );

    if (!response.data.success) {
      return rejectWithValue(response.data.message || "Unable to fetch markets.");
    }

    return response.data.data || [];
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const fetchMarketById = createAsyncThunk<
  Market,
  number,
  { rejectValue: string }
>("markets/fetchMarketById", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<ApiListResponse<Market>>(
      API_ENDPOINTS.markets.detail(id)
    );

    if (!response.data.success) {
      return rejectWithValue(response.data.message || "Unable to fetch market.");
    }

    return response.data.data;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const uploadMarketImage = createAsyncThunk<
  string,
  File,
  { rejectValue: string }
>("markets/uploadMarketImage", async (file, { rejectWithValue }) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axiosInstance.post(
      API_ENDPOINTS.upload.image,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    const uploadedUrl = extractUploadedImageUrl(response.data);

    if (!uploadedUrl) {
      return rejectWithValue("Image upload failed. Please try again.");
    }

    return uploadedUrl;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const createMarket = createAsyncThunk<
  Market,
  MarketPayload,
  { rejectValue: string }
>("markets/createMarket", async (payload, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ApiListResponse<Market>>(
      API_ENDPOINTS.markets.create,
      payload
    );

    if (!response.data.success) {
      return rejectWithValue(response.data.message || "Unable to create market.");
    }

    return response.data.data;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const updateMarket = createAsyncThunk<
  Market,
  { id: number; payload: MarketPayload },
  { rejectValue: string }
>("markets/updateMarket", async ({ id, payload }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<ApiListResponse<Market>>(
      API_ENDPOINTS.markets.update(id),
      payload
    );

    if (!response.data.success) {
      return rejectWithValue(response.data.message || "Unable to update market.");
    }

    return response.data.data;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

export const deleteMarket = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("markets/deleteMarket", async (id, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete<{
      success?: boolean;
      message?: string;
    }>(API_ENDPOINTS.markets.delete(id));

    if (response.data?.success === false) {
      return rejectWithValue(response.data.message || "Unable to delete market.");
    }

    return id;
  } catch (error) {
    return rejectWithValue(getApiErrorMessage(error));
  }
});

const initialState: MarketsState = {
  markets: [],
  selectedMarket: null,
  loading: false,
  detailLoading: false,
  actionLoading: false,
  uploadLoading: false,
  error: null,
};

const marketSlice = createSlice({
  name: "markets",
  initialState,
  reducers: {
    clearMarketError: (state) => {
      state.error = null;
    },
    clearSelectedMarket: (state) => {
      state.selectedMarket = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMarkets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMarkets.fulfilled, (state, action: PayloadAction<Market[]>) => {
        state.markets = action.payload;
        state.loading = false;
      })
      .addCase(fetchMarkets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Unable to fetch markets.";
      })
      .addCase(fetchMarketById.pending, (state) => {
        state.detailLoading = true;
        state.selectedMarket = null;
        state.error = null;
      })
      .addCase(fetchMarketById.fulfilled, (state, action) => {
        state.selectedMarket = action.payload;
        state.detailLoading = false;
      })
      .addCase(fetchMarketById.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.payload || "Unable to fetch market.";
      })
      .addCase(uploadMarketImage.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
      })
      .addCase(uploadMarketImage.fulfilled, (state) => {
        state.uploadLoading = false;
      })
      .addCase(uploadMarketImage.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload || "Image upload failed. Please try again.";
      })
      .addCase(createMarket.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(createMarket.fulfilled, (state, action) => {
        state.markets.push(action.payload);
        state.actionLoading = false;
      })
      .addCase(createMarket.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to create market.";
      })
      .addCase(updateMarket.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(updateMarket.fulfilled, (state, action) => {
        state.markets = state.markets.map((market) =>
          market.id === action.payload.id ? action.payload : market
        );
        state.selectedMarket = action.payload;
        state.actionLoading = false;
      })
      .addCase(updateMarket.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to update market.";
      })
      .addCase(deleteMarket.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(deleteMarket.fulfilled, (state, action) => {
        state.markets = state.markets.filter(
          (market) => market.id !== action.payload
        );
        if (state.selectedMarket?.id === action.payload) {
          state.selectedMarket = null;
        }
        state.actionLoading = false;
      })
      .addCase(deleteMarket.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload || "Unable to delete market.";
      });
  },
});

export const { clearMarketError, clearSelectedMarket } = marketSlice.actions;
export default marketSlice.reducer;
